export interface Loc {
  uk: string;
  en: string;
}

export interface LevelDef {
  level: number;
  name: string;
  threshold: number; // ₴ / month
  cover: string;      // /tiers/<file> banner image
  description: Loc;   // narrative paragraph (collapsible)
  adds: Loc[]; // rewards THIS level adds (cumulative in UI)
}

export interface MerchDef {
  id: string;
  name: Loc;
  unlockLevel: number;
}

export interface RewardDef {
  id: string;
  name: Loc;
  minLevel: number;
  url?: string; // present = tappable
  soon?: boolean; // present = "coming soon"
}

export const LEVELS: LevelDef[] = [
  {
    level: 1, name: "BASE", threshold: 100, cover: "/tiers/base.jpg",
    description: {
      uk: "Малих донатів не буває! Наприклад, завдяки сотні базових підписок ми зберемо суму, якої вистачить, щоб купити дрон-камікадзе. А він знищить ворожу техніку, вартість якої кратна сумі вашого донату.",
      en: "There's no such thing as a small donation. A hundred BASE subscriptions add up to enough for a kamikaze drone — which destroys enemy hardware worth many times your donation.",
    },
    adds: [{ uk: "Доступ до контенту Monobase", en: "Monobase content access" }],
  },
  {
    level: 2, name: "SUPPORTER", threshold: 300, cover: "/tiers/supporter.jpg",
    description: {
      uk: "Iноді просто не вистачає Supporter-а поруч. Рік вашої підписки еквівалентний закупівлі 126 шт. M-Tac хімсвітла — маленького, але надзвичайно корисного засобу. Воно допомагає освітити важкодоступні ділянки, позначити потрібну точку або подати сигнал.",
      en: "Sometimes you just need a Supporter nearby. A year of your subscription equals 126 M-Tac chemlights — small but invaluable for lighting hard-to-reach spots, marking a point, or signalling.",
    },
    adds: [{ uk: "Дизайн картки monobank", en: "monobank card design" }],
  },
  {
    level: 3, name: "INSIDER", threshold: 500, cover: "/tiers/insider.jpg",
    description: {
      uk: "Бути Insider — це вже знати трохи більше, ніж інші. 10 підписників рівня Insider за рік — це комплект нової якісної гуми для пікапу, що вивозить поранених і підвозить допомогу.",
      en: "Being an Insider means knowing a little more than the rest. Ten Insider subscribers over a year cover a full set of quality tyres for a pickup that evacuates the wounded and delivers aid.",
    },
    adds: [{ uk: "Спільнота та ексклюзив", en: "Community & exclusive content" }],
  },
  {
    level: 4, name: "HERO", threshold: 1000, cover: "/tiers/hero.jpg",
    description: {
      uk: "Твоя підписка — це системна дія, яка працює там, де кожна секунда має значення. Для розуміння масштабу: один підписник рівня Hero за рік забезпечує еквівалент 20 турнікетів SICH — це 20 шансів врятувати життя.",
      en: "Your subscription is systematic action where every second counts. For scale: one Hero subscriber over a year provides the equivalent of 20 SICH tourniquets — 20 chances to save a life.",
    },
    adds: [{ uk: "Онлайн-заходи", en: "Online events" }],
  },
  {
    level: 5, name: "SUPERHERO", threshold: 2000, cover: "/tiers/superhero.jpg",
    description: {
      uk: "Твоя підтримка — зв'язок, який ніколи не обривається. Для розуміння масштабу: 10 підписників рівня SuperHero за рік — це приблизно 12 рацій Motorola DP4800e VHF, які допомагають координувати дії там, де швидкість інформування вирішує все.",
      en: "Your support is a connection that never drops. For scale: ten SuperHero subscribers over a year is about 12 Motorola DP4800e VHF radios — the link that coordinates action where speed of information decides everything.",
    },
    adds: [{ uk: "Набір наліпок", en: "Sticker pack" }],
  },
  {
    level: 6, name: "LEGEND", threshold: 5000, cover: "/tiers/legend.jpg",
    description: {
      uk: "Legend — і цим все сказано! Одна підписка Legend за місяць — це, як мінімум, вартість однієї тактичної медичної аптечки. І це лише приклад того, як регулярна підтримка перетворюється на конкретні речі, що рятують життя.",
      en: "Legend — that says it all. One Legend subscription a month is, at minimum, the cost of one tactical medical kit — just one example of how regular support turns into concrete things that save lives.",
    },
    adds: [{ uk: "Фірмовий шопер", en: "Branded tote bag" }],
  },
  {
    level: 7, name: "TITAN", threshold: 29900, cover: "/tiers/titan.jpg",
    description: {
      uk: "«Персональний рахунок» для закупівлі важкого обладнання. Ваша підтримка не просто закриває поточні потреби, а формує ударний кулак. Ваші внески працюють накопичувально: кожні 6 місяців ми конвертуємо зібрану суму в обладнання — Mavic 3T, систему РЕБ або зграю ударних дронів.",
      en: "A \"personal account\" for buying heavy equipment. Your support doesn't just cover current needs — it forms a strike fist. Contributions accumulate: every 6 months we convert the sum into equipment — a Mavic 3T, EW systems, or a swarm of strike drones.",
    },
    adds: [{ uk: "Ексклюзивний шеврон", en: "Exclusive patch" }],
  },
];

export const MERCH: MerchDef[] = [
  {
    id: "stickers",
    name: { uk: "Набір наліпок", en: "Sticker pack" },
    unlockLevel: 3,
  },
  { id: "poster", name: { uk: "Постер", en: "Poster" }, unlockLevel: 4 },
  {
    id: "patch",
    name: { uk: "Шеврон", en: "Embroidered patch" },
    unlockLevel: 4,
  },
  { id: "tshirt", name: { uk: "Футболка", en: "T-shirt" }, unlockLevel: 5 },
  { id: "tote", name: { uk: "Шопер", en: "Tote bag" }, unlockLevel: 5 },
  { id: "cap", name: { uk: "Кепка", en: "Cap" }, unlockLevel: 5 },
];

import { COMMUNITY_URL } from "@/lib/links";

export const REWARDS: RewardDef[] = [
  {
    id: "community",
    name: {
      uk: "Спільнота та ексклюзивний контент",
      en: "Community and Exclusive Content",
    },
    minLevel: 1,
    url: COMMUNITY_URL,
  },
  {
    id: "raffles",
    name: { uk: "Розіграші", en: "Raffles" },
    minLevel: 1,
    soon: true,
  },
  {
    id: "offers",
    name: { uk: "Пропозиції партнерів", en: "Partner offers" },
    minLevel: 2,
    soon: true,
  },
];

export const pickLoc = (l: Loc, lang: string): string =>
  lang === "uk" ? l.uk : l.en;

// rewards visible up to and including a given level (cumulative)
export const cumulativeAdds = (level: number, lang: string): string[] =>
  LEVELS.filter((l) => l.level <= level).flatMap((l) =>
    l.adds.map((a) => pickLoc(a, lang)),
  );
